// @flow
import _, { get, set } from 'lodash';
import * as RequestResultCases from 'middlewares/api-middleware';
import {
  SEARCH_UCD_CAMPAIGNS,
  SEARCH_UCD_CAMPAIGN_GROUPS,
  GET_UCD_PROJECT,
  GET_UCD_PROJECT_METRICS,
  CREATE_UCD_PROJECT,
  SEARCH_UCD_PROJECTS,
  GET_UCD_CONFIGS,
  SUGGEST_UCD_PROJECTS,
  GET_UCD_CAMPAIGN,
  CLEAR_IMAGE,
  CREATE_UCD_CAMPAIGN,
  CREATE_JOURNEY_CAMPAIGN,
  CREATE_JOURNEY_CAMPAIGN_GROUP,
  GET_JOURNEY_CAMPAIGN_GROUP,
  GET_JOURNEY_CAMPAIGN_GROUP_APPROVAL_STATUS,
  GET_JOURNEY_CAMPAIGN_COMMS_TIMELINE,
  GET_JOURNEY_CAMPAIGN_USER_AUDIT,
  CLEAR_JOURNEY_CAMPAIGN_USER_AUDIT,
  PREVIEW_JOURNEY_CAMPAIGN_SHEET_SYNC,
  APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC,
  UPDATE_UCD_PROJECT,
  UPDATE_UCD_CAMPAIGN,
  RESET_NEWLY_CREATED_CAMPAIGN,
  RESET_UCD_CAMPAIGN,
  RESET_JOURNEY_CAMPAIGN_GROUP,
  UPLOAD_IMAGE_TO_HEDWIG,
  UPDATE_UCD_CAMPAIGN_STATUS,
  UPDATE_UCD_PROJECT_STATUS,
  GET_UCD_CAMPAIGN_METRICS,
  GET_UCD_CAMPAIGN_METRICS_BY_DATE,
  GET_UCD_JOURNEY_BUSINESS_METRICS,
  GET_JOURNEY_PROGRESS,
  CREATE_TRIDENT_CAMPAIGN,
  UPDATE_TRIDENT_CAMPAIGN,
  GET_TRIDENT_CAMPAIGN,
  GET_UCD_CAMPAIGN_METRICS_BENCHMARK,
  DEACTIVATE_TRIDENT_CAMPAIGN,
  UCD_GENERATE_MYSTIQUE_CONTENT,
  UCD_GENERATE_MYSTIQUE_CONTENT_FEEDBACK,
  CONTENT_GENERATION_GET_COUNTRIES_CONFIG,
  CONTENT_GENERATION_LIST_PRODUCT_OFFERINGS,
  CONTENT_GENERATION_LIST_ATTRIBUTES,
  CONTENT_GENERATION_GENERATE_MYSTIQUE_V1,
  CONTENT_CONFIG_GENERATE_MYSTIQUE_V2,
  CONTENT_CONFIG_GENERATE_MYSTIQUE_V2_ATTRIBUTE,
  CLEAR_MYSTIQUE_CONTENT,
  SEND_PREVIEW,
  CLEAR_PREVIEW_MESSAGE_STORE,
  CP_GET_VERTICALS,
  CP_GET_COUNTRIES,
  CP_GET_PERSONAS,
  CP_LIST_ATTRIBUTES,
  CP_LIST_CVPS,
  CP_LIST_CVPS_PENDING,
  CP_LIST_PRODUCTS,
  CP_LIST_PRODUCTS_PENDING,
  CP_LIST_AUDIENCES,
  CP_LIST_AUDIENCES_PENDING,
  CP_LIST_EVENTS,
  CP_LIST_EVENTS_PENDING,
  CP_GET_AUDIENCE,
  CP_GET_AUDIENCE_TABLE_INFO,
  CP_CREATE_AUDIENCE,
  CP_UPDATE_AUDIENCE,
  cpAudienceStateKey,
  CONTENT_GENERATION_CONFIG_GET_LATEST,
  CONTENT_GENERATION_CONFIG_LIST_VERSIONS,
  CONTENT_GENERATION_CONFIG_READ_FILE,
  CONTENT_GENERATION_CONFIG_APPLY,
  CONTENT_GENERATION_CONFIG_ROLLBACK,
  CP_AUDIENCE_ALL_SCOPE_KEY,
} from 'omega-rtc/actions/ucd';
import { GET_LIST_RULE_TYPE } from 'omega-rtc/actions/newTridentExtra';
import moment from 'moment';

type UCDState = {
  campaigns: Array,
};

const contentGenerationConfigFileCacheKey = (version, path) => `${version}::${path}`;

const normalizeContentGenerationConfigVersionStatus = (status = '') => String(status || '')
  .replace('CONTENT_GENERATION_CONFIG_VERSION_STATUS_', '')
  .replace('CONTENT_CONFIG_VERSION_STATUS_', '')
  .replace('CONTENT_CONFIG_STATUS_', '');

const isActiveContentGenerationConfigVersion = (configVersion) => (
  !_.get(configVersion, 'status', '')
    || normalizeContentGenerationConfigVersionStatus(_.get(configVersion, 'status', '')) === 'ACTIVE'
);

const upsertContentGenerationConfigVersion = (versions = [], configVersion = null) => {
  const version = Number(_.get(configVersion, 'version', 0));
  if (!version) return versions;
  return _.orderBy(
    _.uniqBy([configVersion, ...versions], 'version'),
    [(item) => Number(_.get(item, 'version', 0))],
    ['desc'],
  );
};

// The { message, status } of a failed content-portal API response.
const apiErrorFromAction = (action) => {
  const response = _.get(action, 'payload.response', {});
  return {
    message: _.get(response, 'data.message')
      || _.get(response, 'data.errors[0].message', ''),
    status: _.get(response, 'status', 0),
  };
};

const defaultUCDState = {
  campaignGroupSearch: { results: [], totalMatch: 0, isPending: false, error: null, searchKey: null },
  campaigns: [],
  campaignMetricsViewByDate: {},
  journeyCampaignProgressView: {},
  projects: [],
  projectSuggestions: [],
  campaignSearchHits: 0,
  projectSearchHits: 0,
  mystiqueRequestTime: null,
  mystiqueRequestPayload: {},
  mystiqueContent: {},
  cpVerticals: [],
  cpCountries: [],
  cpPersonas: [],
  cpAttributes: [],
  cpCvps: [],
  cpCvpsByCountry: {},
  cpProductsByCountry: {},
  cpAudiencesByScope: {},
  cpAudienceByKey: {},
  cpEventsByCountry: {},
  cpAudienceTableInfoBySource: {},
  cpAudienceWriteError: null,
  contentGenerationCountriesConfig: [],
  contentGenerationProductOfferings: [],
  contentGenerationAttributes: [],
  contentGenerationContent: {},
  contentConfigGenerationByAttribute: {},
  contentConfigGenerationErrorsByAttribute: {},
  contentConfigGenerationFailedAttributes: [],
  contentConfigGenerationReadyAttributes: [],
  contentGenerationConfigLatest: null,
  contentGenerationConfigVersions: [],
  contentGenerationConfigVersionsNextPageToken: '',
  contentGenerationConfigFilesByKey: {},
};

// reducer for ucd
const ucdReducer = (
  state: UCDState = defaultUCDState,
  action: FSA,
): UCDState => {
  switch (action.type) {
    case RESET_NEWLY_CREATED_CAMPAIGN:
      return {
        ...state,
        newlyCreatedCampaign: undefined,
      };
    case SEARCH_UCD_CAMPAIGNS + RequestResultCases.SUCCESS:
      return {
        ...state,
        campaigns: _.get(action.payload, 'results', []),
        campaignSearchHits: _.get(action.payload, 'totalMatch', 0),
      };
    case CP_GET_VERTICALS + RequestResultCases.SUCCESS:
      return { ...state, cpVerticals: _.get(action.payload, 'verticals', []) };
    case CP_GET_COUNTRIES + RequestResultCases.SUCCESS:
      return { ...state, cpCountries: _.get(action.payload, 'countries', []) };
    case CP_GET_PERSONAS + RequestResultCases.SUCCESS:
      return { ...state, cpPersonas: _.get(action.payload, 'personas', []) };
    case CP_LIST_ATTRIBUTES + RequestResultCases.SUCCESS:
      return { ...state, cpAttributes: _.get(action.payload, 'attributes', []) };
    case CONTENT_GENERATION_GET_COUNTRIES_CONFIG + RequestResultCases.SUCCESS:
      return { ...state, contentGenerationCountriesConfig: _.get(action.payload, 'countries', []) };
    case CONTENT_GENERATION_LIST_PRODUCT_OFFERINGS + RequestResultCases.REQUEST:
      return { ...state, contentGenerationProductOfferings: [] };
    case CONTENT_GENERATION_LIST_PRODUCT_OFFERINGS + RequestResultCases.SUCCESS:
      return { ...state, contentGenerationProductOfferings: _.get(action.payload, 'productOfferings', []) };
    case CONTENT_GENERATION_LIST_ATTRIBUTES + RequestResultCases.REQUEST:
      return { ...state, contentGenerationAttributes: [] };
    case CONTENT_GENERATION_LIST_ATTRIBUTES + RequestResultCases.SUCCESS:
      return { ...state, contentGenerationAttributes: _.get(action.payload, 'attributes', []) };
    case CP_LIST_CVPS_PENDING + RequestResultCases.SUCCESS:
    case CP_LIST_CVPS + RequestResultCases.SUCCESS: {
      const country = _.get(action, 'meta.country', '');
      const countryKey = country || '__all__';
      const statusKey = _.get(action, 'meta.statusKey', 'active');
      const configs = _.get(action.payload, 'configs', []);
      return {
        ...state,
        cpCvps: countryKey === '__all__' && statusKey === 'active' ? configs : state.cpCvps,
        cpCvpsByCountry: {
          ...state.cpCvpsByCountry,
          [countryKey]: {
            ...(state.cpCvpsByCountry[countryKey] || {}),
            [statusKey]: configs,
          },
        },
      };
    }
    case CP_LIST_PRODUCTS_PENDING + RequestResultCases.SUCCESS:
    case CP_LIST_PRODUCTS + RequestResultCases.SUCCESS: {
      const country = _.get(action, 'meta.country', '');
      const statusKey = _.get(action, 'meta.statusKey', 'active');
      return {
        ...state,
        cpProductsByCountry: {
          ...state.cpProductsByCountry,
          [country]: {
            ...(state.cpProductsByCountry[country] || {}),
            [statusKey]: _.get(action.payload, 'configs', []),
          },
        },
      };
    }
    case CP_LIST_AUDIENCES_PENDING + RequestResultCases.SUCCESS:
    case CP_LIST_AUDIENCES + RequestResultCases.SUCCESS: {
      const scopeKey = _.get(action, 'meta.scopeKey', CP_AUDIENCE_ALL_SCOPE_KEY);
      const statusKey = _.get(action, 'meta.statusKey', 'active');
      return {
        ...state,
        cpAudiencesByScope: {
          ...state.cpAudiencesByScope,
          [scopeKey]: {
            ...(state.cpAudiencesByScope[scopeKey] || {}),
            [statusKey]: _.get(action.payload, 'configs', []),
          },
        },
      };
    }
    case CP_LIST_EVENTS_PENDING + RequestResultCases.SUCCESS:
    case CP_LIST_EVENTS + RequestResultCases.SUCCESS: {
      // An empty country filter lists every country; key that bucket
      // '__all__' so it sits beside the per-country ones.
      const country = _.get(action, 'meta.country', '');
      const countryKey = country || '__all__';
      const statusKey = _.get(action, 'meta.statusKey', 'active');
      return {
        ...state,
        cpEventsByCountry: {
          ...state.cpEventsByCountry,
          [countryKey]: {
            ...(state.cpEventsByCountry[countryKey] || {}),
            [statusKey]: _.get(action.payload, 'configs', []),
          },
        },
      };
    }
    case CP_GET_AUDIENCE + RequestResultCases.SUCCESS: {
      const key = _.get(action, 'meta.key', '');
      if (!key) return state;
      const country = _.get(action, 'meta.country', '');
      return {
        ...state,
        cpAudienceByKey: {
          ...state.cpAudienceByKey,
          [cpAudienceStateKey(country, key)]: _.get(action.payload, 'configs', []),
        },
      };
    }
    case CP_GET_AUDIENCE_TABLE_INFO + RequestResultCases.REQUEST: {
      const table = _.get(action, 'meta.table', '');
      if (!table) return state;
      const previous = _.get(state, ['cpAudienceTableInfoBySource', table], {});
      return {
        ...state,
        cpAudienceTableInfoBySource: {
          ...state.cpAudienceTableInfoBySource,
          [table]: {
            ...previous,
            error: null,
            isLoading: true,
          },
        },
      };
    }
    case CP_GET_AUDIENCE_TABLE_INFO + RequestResultCases.SUCCESS: {
      const table = _.get(action, 'meta.table', '');
      if (!table) return state;
      return {
        ...state,
        cpAudienceTableInfoBySource: {
          ...state.cpAudienceTableInfoBySource,
          [table]: {
            columns: _.get(action.payload, 'columns', []),
            error: null,
            isLoading: false,
          },
        },
      };
    }
    case CP_GET_AUDIENCE_TABLE_INFO + RequestResultCases.FAILURE: {
      const table = _.get(action, 'meta.table', '');
      if (!table) return state;
      return {
        ...state,
        cpAudienceTableInfoBySource: {
          ...state.cpAudienceTableInfoBySource,
          [table]: {
            columns: [],
            error: apiErrorFromAction(action),
            isLoading: false,
          },
        },
      };
    }
    case CP_CREATE_AUDIENCE + RequestResultCases.REQUEST:
    case CP_UPDATE_AUDIENCE + RequestResultCases.REQUEST:
      return { ...state, cpAudienceWriteError: null };
    case CP_CREATE_AUDIENCE + RequestResultCases.FAILURE:
    case CP_UPDATE_AUDIENCE + RequestResultCases.FAILURE:
      return { ...state, cpAudienceWriteError: apiErrorFromAction(action) };
    case CONTENT_GENERATION_CONFIG_GET_LATEST + RequestResultCases.SUCCESS:
      return {
        ...state,
        contentGenerationConfigLatest: action.payload,
      };
    case CONTENT_GENERATION_CONFIG_APPLY + RequestResultCases.SUCCESS: {
      const versions = upsertContentGenerationConfigVersion(
        state.contentGenerationConfigVersions,
        action.payload,
      );
      return {
        ...state,
        ...(isActiveContentGenerationConfigVersion(action.payload)
          ? { contentGenerationConfigLatest: action.payload }
          : {}),
        contentGenerationConfigVersions: versions,
      };
    }
    case CONTENT_GENERATION_CONFIG_ROLLBACK + RequestResultCases.SUCCESS:
      return {
        ...state,
        contentGenerationConfigLatest: action.payload,
        contentGenerationConfigVersions: upsertContentGenerationConfigVersion(
          state.contentGenerationConfigVersions,
          action.payload,
        ),
      };
    case CONTENT_GENERATION_CONFIG_LIST_VERSIONS + RequestResultCases.SUCCESS: {
      const incomingVersions = _.get(action.payload, 'versions', []);
      const previousVersions = _.get(action, 'meta.pageToken', '')
        ? state.contentGenerationConfigVersions
        : [];
      return {
        ...state,
        contentGenerationConfigVersions: _.uniqBy([...previousVersions, ...incomingVersions], 'version'),
        contentGenerationConfigVersionsNextPageToken: _.get(action.payload, 'next_page_token', ''),
      };
    }
    case CONTENT_GENERATION_CONFIG_READ_FILE + RequestResultCases.SUCCESS: {
      const version = _.get(action, 'meta.version', '');
      const path = _.get(action, 'meta.path', '');
      const file = _.get(action.payload, 'file', null);
      if (!version || !path || !file) return state;
      const cacheKey = contentGenerationConfigFileCacheKey(version, path);
      return {
        ...state,
        contentGenerationConfigFilesByKey: {
          ...state.contentGenerationConfigFilesByKey,
          [cacheKey]: file,
        },
      };
    }
    case GET_UCD_PROJECT + RequestResultCases.SUCCESS:
      return {
        ...state,
        projectView: action.payload,
      };
    case GET_UCD_PROJECT_METRICS + RequestResultCases.SUCCESS:
      return {
        ...state,
        projectMetricsView: action.payload,
      };
    case GET_UCD_CAMPAIGN_METRICS + RequestResultCases.SUCCESS:
      return {
        ...state,
        campaignMetricsView: action.payload,
      };
    case GET_UCD_JOURNEY_BUSINESS_METRICS + RequestResultCases.SUCCESS:
      return {
        ...state,
        journeyCampaignBusinessMetricsView: action.payload,
      };
    case GET_UCD_CAMPAIGN_METRICS_BY_DATE + RequestResultCases.SUCCESS: {
      const dateRange = `${moment(action.meta.request.params.startTime).format('YYYY/MM/DD')} - ${moment(action.meta.request.params.endTime).format('YYYY/MM/DD')}`;
      return {
        ...state,
        campaignMetricsViewByDate: {
          ...state.campaignMetricsViewByDate,
          [dateRange]: action.payload,
        },
      };
    }
    case GET_UCD_CAMPAIGN_METRICS_BENCHMARK + RequestResultCases.SUCCESS:
      return {
        ...state,
        campaignMetricsBenchmark: action.payload,
      };
    case SEARCH_UCD_CAMPAIGN_GROUPS + RequestResultCases.REQUEST:
      return {
        ...state,
        campaignGroupSearch: {
          results: [],
          totalMatch: 0,
          isPending: true,
          error: null,
          searchKey: action.meta.searchKey,
          requestTime: action.meta.requestTime,
        },
      };
    case SEARCH_UCD_CAMPAIGN_GROUPS + RequestResultCases.SUCCESS:
    case SEARCH_UCD_CAMPAIGN_GROUPS + RequestResultCases.FAILURE: {
      // A slow response to an older filter/page must not replace the current listing.
      if (action.meta.searchKey !== state.campaignGroupSearch.searchKey
        || action.meta.requestTime !== state.campaignGroupSearch.requestTime) return state;
      const failed = action.type.endsWith(RequestResultCases.FAILURE);
      return {
        ...state,
        campaignGroupSearch: {
          ...state.campaignGroupSearch,
          isPending: false,
          results: failed ? [] : (action.payload.results || []),
          totalMatch: failed ? 0 : Number(action.payload.totalMatch || 0),
          error: failed ? (apiErrorFromAction(action).message || 'Unable to load campaign groups') : null,
        },
      };
    }
    case SEARCH_UCD_PROJECTS + RequestResultCases.SUCCESS:
      return {
        ...state,
        projects: _.get(action.payload, 'results', []),
        projectSearchHits: _.get(action.payload, 'totalMatch', 0),
      };
    case CREATE_UCD_PROJECT + RequestResultCases.SUCCESS:
      return {
        ...state,
        newProjectID: _.get(action.payload, 'ID', ''),
        createProjectError: undefined,
      };
    case UPDATE_UCD_PROJECT + RequestResultCases.SUCCESS:
      return {
        ...state,
        updatedProjectID: _.get(action.payload, 'ID', ''),
      };
    case CREATE_UCD_CAMPAIGN + RequestResultCases.SUCCESS:
      return {
        ...state,
        newlyCreatedCampaign: _.get(action.payload, 'ID', ''),
        createCampaignError: undefined,
      };
    case CREATE_TRIDENT_CAMPAIGN + RequestResultCases.SUCCESS:
      return {
        ...state,
        newlyCreatedTridentCampaign: _.get(action.payload, 'ID', ''),
        createTridentCampaignError: undefined,
      };
    case CREATE_JOURNEY_CAMPAIGN_GROUP + RequestResultCases.SUCCESS:
      return {
        ...state,
        newlyCreatedCampaignGroup: _.get(action.payload, 'campaignGroupID', ''),
        createJourneyCampaignGroupError: undefined,
      };
    case GET_JOURNEY_CAMPAIGN_GROUP + RequestResultCases.SUCCESS:
      return {
        ...state,
        journeyCampaignGroupView: action.payload,
      };
    case GET_JOURNEY_CAMPAIGN_GROUP_APPROVAL_STATUS + RequestResultCases.REQUEST: {
      const previous = state.journeyCampaignGroupApprovalStatus;
      const campaigns = previous && previous.campaignGroup === action.meta.campaignGroup
        ? previous.campaigns : {};
      return {
        ...state,
        journeyCampaignGroupApprovalStatus: {
          campaignGroup: action.meta.campaignGroup,
          campaigns: {
            ...campaigns,
            [action.meta.campaign.campaignID]: {
              requestTime: action.meta.requestTime,
              isPending: true,
            },
          },
        },
      };
    }
    case GET_JOURNEY_CAMPAIGN_GROUP_APPROVAL_STATUS + RequestResultCases.SUCCESS:
    case GET_JOURNEY_CAMPAIGN_GROUP_APPROVAL_STATUS + RequestResultCases.FAILURE: {
      const previous = state.journeyCampaignGroupApprovalStatus;
      const campaignID = action.meta.campaign.campaignID;
      const request = previous && previous.campaigns[campaignID];
      if (!request || request.requestTime !== action.meta.requestTime
        || previous.campaignGroup !== action.meta.campaignGroup) return state;
      return {
        ...state,
        journeyCampaignGroupApprovalStatus: {
          ...previous,
          campaigns: {
            ...previous.campaigns,
            [campaignID]: {
              ...request,
              isPending: false,
              isFailure: action.type.endsWith(RequestResultCases.FAILURE),
              details: action.payload,
            },
          },
        },
      };
    }
    case UPDATE_UCD_CAMPAIGN + RequestResultCases.SUCCESS:
      return {
        ...state,
        updatedCampaignID: _.get(action.payload, 'ID', ''),
      };
    case GET_UCD_CONFIGS + RequestResultCases.SUCCESS:
      return {
        ...state,
        configs: action.payload,
      };
    case SUGGEST_UCD_PROJECTS + RequestResultCases.SUCCESS:
      return {
        ...state,
        projectSuggestions: _.get(action.payload, 'results', []),
      };
    case GET_UCD_CAMPAIGN + RequestResultCases.SUCCESS:
      return {
        ...state,
        campaignView: action.payload,
      };
    case GET_JOURNEY_CAMPAIGN_COMMS_TIMELINE + RequestResultCases.SUCCESS:
      return { ...state, journeyCampaignCommsTimeline: action.payload };
    case CLEAR_JOURNEY_CAMPAIGN_USER_AUDIT:
      return {
        ...state,
        journeyCampaignUserAudit: undefined,
        journeyCampaignUserAuditRequest: undefined,
      };
    case GET_JOURNEY_CAMPAIGN_USER_AUDIT + RequestResultCases.REQUEST:
      return {
        ...state,
        journeyCampaignUserAudit: undefined,
        journeyCampaignUserAuditRequest: {
          isPending: true,
          requestTime: _.get(action, 'meta.requestTime'),
        },
      };
    case GET_JOURNEY_CAMPAIGN_USER_AUDIT + RequestResultCases.SUCCESS:
      if (_.get(state, 'journeyCampaignUserAuditRequest.requestTime')
        !== _.get(action, 'meta.requestTime')
        || !state.journeyCampaignUserAuditRequest) {
        return state;
      }
      return {
        ...state,
        journeyCampaignUserAudit: action.payload || { events: [] },
        journeyCampaignUserAuditRequest: {
          isSuccessful: true,
          requestTime: _.get(action, 'meta.requestTime'),
        },
      };
    case GET_JOURNEY_CAMPAIGN_USER_AUDIT + RequestResultCases.FAILURE:
      if (_.get(state, 'journeyCampaignUserAuditRequest.requestTime')
        !== _.get(action, 'meta.requestTime')
        || !state.journeyCampaignUserAuditRequest) {
        return state;
      }
      return {
        ...state,
        journeyCampaignUserAudit: undefined,
        journeyCampaignUserAuditRequest: {
          isFailure: true,
          requestTime: _.get(action, 'meta.requestTime'),
        },
      };
    case PREVIEW_JOURNEY_CAMPAIGN_SHEET_SYNC + RequestResultCases.REQUEST:
      return {
        ...state,
        journeyCampaignSheetSyncApplied: undefined,
        journeyCampaignSheetSyncApplyError: undefined,
        journeyCampaignSheetSyncPreview: undefined,
      };
    case PREVIEW_JOURNEY_CAMPAIGN_SHEET_SYNC + RequestResultCases.SUCCESS:
      return { ...state, journeyCampaignSheetSyncPreview: action.payload };
    case APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC + RequestResultCases.REQUEST:
      return { ...state, journeyCampaignSheetSyncApplied: undefined, journeyCampaignSheetSyncApplyError: undefined };
    case APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC + RequestResultCases.FAILURE:
      return { ...state, journeyCampaignSheetSyncApplied: undefined, journeyCampaignSheetSyncApplyError: action.payload };
    case APPLY_JOURNEY_CAMPAIGN_SHEET_SYNC + RequestResultCases.SUCCESS:
      return { ...state, journeyCampaignSheetSyncApplied: action.payload, journeyCampaignSheetSyncApplyError: undefined };
    case RESET_UCD_CAMPAIGN:
      return {
        ...state,
        campaignView: undefined,
        journeyCampaignCommsTimeline: undefined,
        journeyCampaignUserAudit: undefined,
        journeyCampaignUserAuditRequest: undefined,
        journeyCampaignSheetSyncPreview: undefined,
        journeyCampaignSheetSyncApplied: undefined,
        journeyCampaignSheetSyncApplyError: undefined,
      };
    case RESET_JOURNEY_CAMPAIGN_GROUP:
      return {
        ...state,
        journeyCampaignGroupView: undefined,
        journeyCampaignGroupApprovalStatus: undefined,
        journeyCampaignSheetSyncApplied: undefined,
        journeyCampaignSheetSyncApplyError: undefined,
        journeyCampaignSheetSyncPreview: undefined,
      };
    case GET_LIST_RULE_TYPE + RequestResultCases.SUCCESS: {
      const ruleTypesObj = {};
      const listEventConditionActionResponse = _.get(
        action,
        'payload.listEventConditionActionResponse',
      );
      const targetUserType = get(
        action,
        'meta.request.data.listEventConditionActionRequest.targetUserType',
      );
      set(ruleTypesObj, targetUserType, listEventConditionActionResponse);
      return {
        ...state,
        listRuleTypes: ruleTypesObj,
      };
    }
    case UPDATE_UCD_CAMPAIGN_STATUS + RequestResultCases.SUCCESS:
      return {
        ...state,
        updatedCampaignStatusTimestamp: Date.now(),
      };
    case UPDATE_UCD_PROJECT_STATUS + RequestResultCases.SUCCESS:
      return {
        ...state,
        updatedProjectStatusTimestamp: Date.now(),
      };

    case CREATE_UCD_PROJECT + RequestResultCases.FAILURE:
      return {
        ...state,
        createProjectError: _.get(action.payload, 'response.data.message'),
      };
    case CREATE_UCD_CAMPAIGN + RequestResultCases.FAILURE:
      return {
        ...state,
        createCampaignError: _.get(action.payload, 'response.data.message'),
      };
    case CREATE_JOURNEY_CAMPAIGN_GROUP + RequestResultCases.FAILURE:
      return {
        ...state,
        createJourneyCampaignGroupError: _.get(action.payload, 'response.data.message'),
      };
    case CREATE_JOURNEY_CAMPAIGN + RequestResultCases.FAILURE:
      return {
        ...state,
        createJouneyCampaignRequest: action.meta.request.data,
        tridentCampaignView: action.meta.request.data || null,
      };
    case CREATE_TRIDENT_CAMPAIGN + RequestResultCases.FAILURE:
      return {
        ...state,
        createTridentCampaignError: _.get(
          action.payload,
          'response.data.message',
        ),
      };
    case UPDATE_TRIDENT_CAMPAIGN + RequestResultCases.SUCCESS:
      return {
        ...state, // TODO: Consider to remove it later
      };
    case DEACTIVATE_TRIDENT_CAMPAIGN + RequestResultCases.SUCCESS:
      return {
        ...state,
      };
    case GET_TRIDENT_CAMPAIGN + RequestResultCases.SUCCESS:
      return {
        ...state,
        tridentCampaignView: action.payload,
      };
    case GET_UCD_JOURNEY_BUSINESS_METRICS + RequestResultCases.FAILURE: {
      return {
        ...state,
        journeyCampaignBusinessMetricsView: {
          business: {
            newUsers: 123,
            gmv: 123,
            transactions: 123,
            revenue: 500,
          },
        },
      };
    }
    case GET_JOURNEY_PROGRESS + RequestResultCases.SUCCESS:
      return {
        ...state,
        journeyCampaignProgressView: action.payload,
      };
    case GET_JOURNEY_PROGRESS + RequestResultCases.FAILURE: {
      return {
        ...state,
        journeyCampaignProgressView: {
          metrics: {},
        },
      };
    }
    case UCD_GENERATE_MYSTIQUE_CONTENT + RequestResultCases.REQUEST:
    case CONTENT_GENERATION_GENERATE_MYSTIQUE_V1 + RequestResultCases.REQUEST:
    case UCD_GENERATE_MYSTIQUE_CONTENT_FEEDBACK + RequestResultCases.REQUEST:
      return {
        ...state,
        mystiqueRequestTime: action.meta.requestTime,
        mystiqueRequestPayload: action.payload.data,
      };

    case UCD_GENERATE_MYSTIQUE_CONTENT + RequestResultCases.SUCCESS:
      return {
        ...state,
        mystiqueContent: action.payload,
        mystiqueRequestTime: action.meta.requestTime,
      };
    case CONTENT_GENERATION_GENERATE_MYSTIQUE_V1 + RequestResultCases.SUCCESS:
      return {
        ...state,
        contentGenerationContent: action.payload,
        mystiqueRequestTime: action.meta.requestTime,
      };
    case CONTENT_CONFIG_GENERATE_MYSTIQUE_V2 + RequestResultCases.REQUEST: {
      const requestedAttributes = new Set(_.get(action, 'meta.attributes', []));
      const preserveExisting = _.get(action, 'meta.preserveExisting', false);
      return {
        ...state,
        contentConfigGenerationByAttribute: preserveExisting
          ? state.contentConfigGenerationByAttribute
          : {},
        contentConfigGenerationErrorsByAttribute: preserveExisting
          ? _.omit(state.contentConfigGenerationErrorsByAttribute, [...requestedAttributes])
          : {},
        contentConfigGenerationFailedAttributes: preserveExisting
          ? state.contentConfigGenerationFailedAttributes
            .filter((attribute) => !requestedAttributes.has(attribute))
          : [],
        contentConfigGenerationReadyAttributes: preserveExisting
          ? state.contentConfigGenerationReadyAttributes
            .filter((attribute) => !requestedAttributes.has(attribute))
          : [],
      };
    }
    case CONTENT_CONFIG_GENERATE_MYSTIQUE_V2_ATTRIBUTE + RequestResultCases.SUCCESS: {
      const attribute = _.get(action, 'meta.attribute', '');
      return {
        ...state,
        contentConfigGenerationByAttribute: {
          ...state.contentConfigGenerationByAttribute,
          [attribute]: action.payload || {},
        },
        contentConfigGenerationErrorsByAttribute: _.omit(
          state.contentConfigGenerationErrorsByAttribute,
          [attribute],
        ),
        contentConfigGenerationReadyAttributes: _.uniq([
          ...state.contentConfigGenerationReadyAttributes,
          attribute,
        ]),
        mystiqueRequestTime: action.meta.requestTime,
      };
    }
    case CONTENT_CONFIG_GENERATE_MYSTIQUE_V2_ATTRIBUTE + RequestResultCases.FAILURE: {
      const attribute = _.get(action, 'meta.attribute', '');
      // The quiet error handler forwards the Axios error, so Mystique's own
      // 422 reason is available for the generation modal.
      const errorMessage = _.get(action, 'payload.response.data.error', '');
      return {
        ...state,
        contentConfigGenerationErrorsByAttribute: errorMessage
          ? { ...state.contentConfigGenerationErrorsByAttribute, [attribute]: errorMessage }
          : state.contentConfigGenerationErrorsByAttribute,
        contentConfigGenerationFailedAttributes: _.uniq([
          ...state.contentConfigGenerationFailedAttributes,
          attribute,
        ]),
      };
    }
    case UCD_GENERATE_MYSTIQUE_CONTENT_FEEDBACK + RequestResultCases.SUCCESS:
      return {
        ...state,
        mystiqueContent: action.payload,
        mystiqueRequestTime: action.meta.requestTime,
      };
    case CLEAR_MYSTIQUE_CONTENT:
      return {
        ...state,
        mystiqueContent: {},
        mystiqueRequestTime: null,
      };
    case UPLOAD_IMAGE_TO_HEDWIG + RequestResultCases.SUCCESS:
      return {
        ...state,
        image: action.payload,
      };
    case CLEAR_IMAGE:
      return {
        ...state,
        image: {},
      };
    case SEND_PREVIEW + RequestResultCases.SUCCESS: {
      return {
        ...state,
        deliveredHedwigMessages: [
          ...(state.deliveredHedwigMessages || []),
          {
            message: action.payload.messageID,
            requestID: action.meta.requestID,
          },
        ],
      };
    }
    case SEND_PREVIEW + RequestResultCases.FAILURE: {
      return {
        ...state,
        undeliveredHedwigMessages: [
          ...(state.undeliveredHedwigMessages || []), // request ID's of undelivered messages
          action.meta.requestID,
        ],
      };
    }
    case CLEAR_PREVIEW_MESSAGE_STORE: {
      return {
        ...state,
        deliveredHedwigMessages: [],
        undeliveredHedwigMessages: [],
      };
    }

    default:
      return state;
  }
};

export default ucdReducer;
